import { Button, Card, Col, Dropdown, List, Tag, Tabs } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import themes from 'assets/styles/theme';
import { LS_THEME_CODE, REGULAR_SECTION_ROW_VERTICAL_SPACING } from 'constants/config';

const theme = localStorage.getItem(LS_THEME_CODE) || '';

export const BadgeBox = styled.span`
  color: #fff;
  font-size: 0.6em;
  font-weight: 500;
  background-color: #635e5e;
  border-radius: ${themes['@border-radius-base']};
  padding: 2px;
`;

export const BlockButtonLeftSpace = styled.span`
  display: inherit;
  padding-left: 2px;
`;

export const ButtonBox = styled.div`
  & .ant-btn {
    margin: 4px;
  }
`;

export const ButtonIcon = styled.i`
  margin-right: 8px;
`;

export const CardWrapper = styled.div`
  padding: 10px;
`;

export const CompactButton = styled(Button)`
  &.ant-btn {
    padding: 4px 2px;
  }
`;

export const DangerButton = styled(Button)`
  &.ant-btn {
    color: #fff;
    background-color: ${themes['@danger-color']};
    border-color: #bd2130;
  }

  &.ant-btn:disabled {
    background-color: inherit !important;
    border-color: #d9d9d9;
    color: rgba(0, 0, 0, .25);
  }

  &.ant-btn-primary[disabled] {
    color: rgba(0, 0, 0, 0.25);
    background: #f5f5f5;
    border-color: #d9d9d9;
  }
`;

export const DangerDropdownButton = styled(Dropdown.Button)`
  &.ant-dropdown-button > .ant-btn:first-child {
    color: #fff;
    background-color: ${themes['@danger-color']};
    border-color: #bd2130;
  }

  &.ant-dropdown-button > .ant-btn:nth-child(2):hover {
    border-color: ${themes['@danger-color']};
    color: ${themes['@danger-color']};
  }

  &.ant-dropdown-button > .ant-btn:disabled {
    background-color: inherit !important;
    border-color: #d9d9d9;
    color: rgba(0, 0, 0, .25);
  }

  &.ant-dropdown-button .anticon {
    font-size: 1.2em;
  }

  & .ant-btn-primary[disabled] {
    color: rgba(0, 0, 0, 0.25);
    background: #f5f5f5;
    border-color: #d9d9d9;
  }
`;

export const DangerIcon = styled(ExclamationCircleOutlined)`
  color: ${themes['@danger-color']} !important;
`;

export const WarningIcon = styled(ExclamationCircleOutlined)`
  color: ${themes['@warning-color']} !important;
`;

export const DetailBox = styled(Tag)`
  &:hover {
    background-color: #55acee;
    color: #fff;
    cursor: pointer;
  }
`;

export const ErrorMessage = styled.span`
  color: #f00;
`;

export const FormCol = styled(Col)`
  & .group-compact-select-left .ant-select-selector {
    border-bottom-right-radius: 0;
    border-right: none;
    border-top-right-radius: 0;
  }

  & .group-compact-select-left.form-selection200 {
    text-align: left;
    width: 200px;
  }

  & .group-compact-select-left.form-selection200 .ant-select-selection-item {
    width: 180px;
  }

  & .group-compact-right {
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
  }

  & .group-compact-right.form-input200 {
    width: 200px;
  }

  & .group-compact-right.form-input400 {
    width: 400px;
  }
`;

export const GreyCardWrapper = theme === 'new' ?
  styled.div`
    background-color: ${(props) => props.color || '#FFF'};
    padding: 20px;
    background: white;
    margin: 10px;
    border-radius: 10px;
    box-shadow: 0px 2px 5px 1px rgba(51, 95, 226, 0.2);
  ` :
  styled.div`
    padding: 8px;
    background-color: ${(props) => props.color || '#FFF'};
    border-radius: ${themes['@border-radius-base']};
  `

export const CardSection = styled(GreyCardWrapper)`
  & .compact-card .ant-card-extra {
    padding: 0;
  }

  & .compact-card .ant-card-head {
    min-height: min-content;
  }

  & .compact-card .ant-card-head-title {
    padding: 10px 0;
  }

  & .t-source-card .ant-card-extra {
    width: calc(100% - 100px);
  }
`;

export const ClassicCard = styled(Card)`
  padding: 24px;
  margin: 0;
  color: rgba(0, 0, 0, 0.85);
  font-size: 14px;
  font-variant: tabular-nums;
  line-height: 1.5715;
  list-style: none;
  font-feature-settings: 'tnum';
  position: relative;
  background: #fff;
  border-radius: 10px;
  border: 1px solid #f0f0f0;
  box-shadow: unset;

  & .form-label-col-sel-end {
    position: relative;
  }

  & .form-label-col-sel-end label {
    width: 100%;
  }

  & .form-label-col-sel-end .ant-select {
    padding: 0;
    position: absolute;
    width: 100%;
  }
`;

export const GridIconBox = styled.span`
  margin-left: 10px;
  color: ${(props) => props.color || '#000'};

  &:hover {
    color: ${(props) => props.color || '#00F'};
    cursor: pointer;
  }
`;

export const Label = styled.label`
  font-size: ${(props) => props.theme.fontSize || '1em'};
    
  &.clickable,
  & .clickable {
    cursor: pointer;
  }

  &.label-disabled,
  & .label-disabled {
    color: rgba(0, 0, 0, .25);
    cursor: not-allowed;
  }

  &.label-primary,
  & .label-primary {
    color: ${themes['@primary-color']};
  }

  & span {
    font-size: ${(props) => props.theme.fontSize || '1em'};
  }

  &.label-bold,
  & .label-bold {
    font-weight: 600;
  }

  &.label-danger,
  & .label-danger {
    color: ${themes['@danger-color']};
  }

  &.label-fs1_2,
  & .label-fs1_2 {
    font-size: 1.2em;
  }

  &.label-fs1_3,
  & .label-fs1_3 {
    font-size: 1.3em;
  }

  &.label-fs2,
  & .label-fs2 {
    font-size: 2em;
  }

  &.label-grey,
  & .label-grey {
    color: #666;
  }

  &.label-green1,
  & .label-green1 {
    color: rgb(86, 149, 34);
  }

  &.label-required::before {
    color: ${themes['@danger-color']};
    content: '* ';
  }

  &.label-success,
  & .label-success {
    color: ${themes['@success-color']};
  }

  &.label-warning,
  & .label-warning {
    color: ${themes['@warning-color']};
  }

  &.tail-colon::after {
    content: ': ';
  }

  &.tail-colon1::after {
    content: ':';
    margin-right: 4px;
  }

  &.tail-colon2::after {
    content: ': ';
    margin-right: 8px;
  }
`;

export const HeaderTitleBar = styled.div`
  background-color: #fff;
  padding: 6px 10px;
  border-radius: ${themes['@border-radius-base']};

  & h1,
  .ant-breadcrumb {
    font-family: Lato, sans-serif;
    font-size: 20px;
    font-weight: 800;
    margin-bottom: 0;
  }

  & h1 {
    color: #2d3f5d;
  }

  & .act-box {
    color: #131313;
  }

  & .act-box:hover {
    color: #49a8ff;
    cursor: pointer;
  }

  & .anticon-arrow-left {
    font-size: 24px;
    padding: 0 20px 0 0;
  }
`;

export const InfoButton = styled(Button)`
  &.ant-btn {
    color: #fff;
    background-color: ${themes['@info-color']};
    border-color: #117a8b;
  }

  &.ant-btn:disabled {
    background-color: inherit !important;
    border-color: #d9d9d9;
    color: rgba(0, 0, 0, .25);
  }

  &.ant-btn-primary[disabled] {
    color: rgba(0, 0, 0, 0.25);
    background: #f5f5f5;
    border-color: #d9d9d9;
  }
`;

export const LinkText = styled.span`
  cursor: pointer;
`;

export const MainList = styled(List)`
  & .ant-list-item {
    padding: 6px;
  }

  & .ant-list-item:nth-child(odd) {
    background-color: #ececec;
  }
`;

export const MainTabs = styled(Tabs)`
  &.ant-tabs > .ant-tabs-nav {
    margin-bottom: 0;
  }
`;

export const ModalDialogBodyCtn = styled.div`
  height: 100%;
  position: relative;
`;

export const ModalTitle = styled.h3`
  font-weight: bold;
  margin-bottom: 0;
`;

export const SuccessButton = styled(Button)`
  &.ant-btn {
    color: #fff;
    background-color: ${themes['@success-color']};
    border-color: #1e7e34;
  }

  &.ant-btn[disabled], &.ant-btn[disabled]:hover, &.ant-btn[disabled]:focus, &.ant-btn[disabled]:active {
    color: rgba(0, 0, 0, 0.25);
    border-color: #d9d9d9;
    background: #f5f5f5;
    text-shadow: none;
    box-shadow: none;
  }
`;

export const SecondaryButton = styled(Button)`
  &.ant-btn {
    color: #fff;
    background-color: ${themes['@secondary-color']};
    border-color: #545b62;
  }

  &.ant-btn[disabled], &.ant-btn[disabled]:hover, &.ant-btn[disabled]:focus, &.ant-btn[disabled]:active {
    color: rgba(0, 0, 0, 0.25);
    border-color: #d9d9d9;
    background: #f5f5f5;
    text-shadow: none;
    box-shadow: none;
  }
`;

export const SiteContent = styled.div`
  min-height: calc(100vh - 105px);

  &.common-content {
    min-height: auto;
  }

  &.common-hcontent,
  & .common-hcontent-box {
    height: calc(100vh - 75px);
  }

  & .ant-input-group-wrapper .ant-btn {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  & .ant-input-group-wrapper .ant-btn:first-child {
    border-bottom-left-radius: ${themes['@border-radius-base']};
    border-bottom-right-radius: 0;
    border-top-left-radius: ${themes['@border-radius-base']};
    border-top-right-radius: 0;
  }

  & .ant-input-group-wrapper .ant-btn:last-child {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: ${themes['@border-radius-base']};
    border-top-left-radius: 0;
    border-top-right-radius: ${themes['@border-radius-base']};
  }

  & .b-transparent {
    border-color: transparent;
  }

  & .bb-dashed {
    //border-bottom: 1px dashed #D9D9D9;
    border-bottom: 1px dashed #C6C6C6;
  }

  & .bb-solid-f0 {
    border-bottom: 1px solid #F0F0F0;
  }

  & .bblr-0 {
    border-bottom-left-radius: 0;
  }

  & .bbrr-0 {
    border-bottom-right-radius: 0;
  }

  & .bg-daybreak-blue-1 {
    background-color: #BAE0FF;
  }

  & .bg-error {
    background-color: #FFF2F0;
  }

  & .bg-error .InovuaReactDataGrid__row-hover-target:hover {
    background-color: #FFE2F0 !important;
  }

  & .bg-error1 {
    background-color: #FFE2F0;
  }

  & .bg-error1 .InovuaReactDataGrid__row-hover-target:hover {
    background-color: #FFD2F0 !important;
  }

  & .bg-item-1:nth-child(odd) {
    background-color: #BAE0FF;
  }

  & .bg-item-1:nth-child(even) {
    background-color: #B5F5EC;
  }

  & .bg-item-2:nth-child(even) {
    background-color: rgba(220, 220, 220, .2);
  }

  & .bg-no {
    background: none;
  }

  & .blr-0 {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  & .br-0 {
    border-radius: 0;
  }

  & .br-10 {
    border-radius: ${themes['@border-radius-base']};
  }

  & .br-5 {
    border-radius: 5px;
  }

  & .brr-0 {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  & .btlr-0 {
    border-top-left-radius: 0;
  }

  & .btrr-0 {
    border-top-right-radius: 0;
  }

  & .c-list-item:nth-child(odd) {
    background-color: #F8F8F8;
  }

  & .cell-1-icon {
    width: calc(100% - 14px);
  }

  & .cursor-pointer {
    cursor: pointer;
  }

  & .dashboard-card {
    border: none;
    box-shadow: 0px 2px 5px 1px rgba(51, 95, 226, 0.2);
  }

  & .dashboard-card .ant-card-extra {
    padding: 4px 0 !important;
  }

  & .dashboard-card .ant-card-head {
    color: #184A4E;
    font-size: 21px;
    font-weight: 600;
    margin-bottom: initial;
    min-height: auto;
    padding-left: 10px;
  }

  & .dashboard-card .ant-card-head-title {
    padding: 0 !important;
  }

  & .dashboard-card > .card-head {
    border-radius: 10px 10px 0 0;
  }

  & .dashboard-card > .card-head > h5.ant-typography {
    margin-bottom: 0;
  }

  & .dashboard-card > .card-head .anticon {
    font-size: 21px;
  }

  & .dashboard-card .ant-collapse-header {
    align-items: center;
  }

  & .dashboard-card .ant-collapse-header h5.ant-typography {
    margin-bottom: 0;
  }

  & .dashboard-card .ant-table-container table > thead > tr:first-child th:first-child {
    border-top-left-radius: 0;
  }

  & .dashboard-card .ant-table-container table > thead > tr:first-child th:last-child {
    border-top-right-radius: 0;
  }

  & .dashboard-card .body-center .ant-card-body {
    align-items: center;
    display: flex;
    justify-content: space-around;
  }

  & .dashboard-card .statistic-card {
    align-items: center;
    border: none;
    background-color: white;
    box-shadow: rgba(51, 95, 226, 0.2) 0px 2px 5px 1px;
    color: rgba(31, 31, 31, 0.85);
  }

  & .dashboard-card .statistic-card .ant-card-body {
    display: flex;
    flex-direction: column;
    padding: 6px 10px;
    height: 100%;
    justify-content: space-around;
    //font-size: 18px;
  }

  & .dashboard-card .statistic-card.v-center .ant-card-body {
    align-items: center;
  }

  & .dashboard-card.bg-transparent,
  & .dashboard-card.bg-transparent .ant-card-head {
    background-color: transparent;
  }

  & .dashboard-card.card-body1 > .ant-card-body {
    padding: 10px;
  }

  & .dashboard-card.head1 > .ant-card-head {
    padding: 8px 10px !important;
  }

  & .dashboard-card.head2 > .ant-card-head {
    padding: 4px 15px !important;
  }

  & .dashboard-card.no-box-shadow,
  & .statistic-card.no-box-shadow {
    box-shadow: none;
  }

  & .dashboard-card.sub-head1 > .ant-card-head {
    padding: 4px 15px !important;
  }

  & .dashboard-card.subtitle1 > .ant-card-head,
  & .dashboard-card.subtitle2 > .ant-card-head,
  & .dashboard-card.subtitle3 > .ant-card-head,
  & .dashboard-card.subtitle4 > .ant-card-head,
  & .dashboard-card.subtitle5 > .ant-card-head {
    background: rgba(44, 105, 154, 0.2);
    border-bottom: none;
    border-radius: 10px 10px 0 0;
    padding: 4px 25px;
  }

  & .dashboard-card.subtitle2 > .ant-card-head,
  & .dashboard-card.subtitle2 > .card-head {
    background: rgba(195, 143, 1, 0.2);
  }

  & .dashboard-card.subtitle3 > .ant-card-head {
    background: rgba(24, 74, 78, 0.15);
  }

  & .dashboard-card.subtitle4 > .ant-card-head,
  & .dashboard-card.subtitle4 > .card-head {
    background: rgba(0, 116, 61, 0.2);
  }

  & .dashboard-card.subtitle5 > .ant-card-head,
  & .dashboard-card.subtitle5 > .card-head {
    background: rgba(74, 61, 130, 0.2);
  }

  & .font-btn-icon {
    margin-left: 4px;
  }

  & .form-label-selector .ant-select-selection-item {
    font-weight: 600;
    text-align: right;
  }

  & .form-sel-label-col .ant-select {
    position: absolute;
  }

  & .form-sel-label-col label {
    width: 100%;
  }

  & .fs-15 {
    font-size: 15px;
  }

  & .fs-16 {
    font-size: 16px;
  }

  & .fs-18 {
    font-size: 18px;
  }

  & .fs-20 {
    font-size: 20px;
  }

  & .fs-22 {
    font-size: 22px;
  }

  & .fs-24 {
    font-size: 24px;
  }

  & .fs-26 {
    font-size: 26px;
  }

  & .fs-28 {
    font-size: 28px;
  }

  & .fs-30 {
    font-size: 30px;
  }

  & .fs-32 {
    font-size: 32px;
  }

  & .fw-400 {
    font-weight: 400;
  }

  & .fw-600 {
    font-weight: 600;
  }

  & .h-100p {
    height: 100%;
  }

  & .h-24 {
    height: 24px;
  }

  & .h-28 {
    height: 28px;
  }

  & .h-30 {
    height: 30px;
  }

  & .inline-block {
    display: inline-block;
  }

  & .input-addon-btn .ant-input-group-addon {
    padding: 0;
  }

  & .m-0-1 {
    margin: 0 1px;
  }

  & .m-0-2 {
    margin: 0 2px;
  }

  & .mb-2 {
    margin-bottom: 2px;
  }

  & .mb-16 {
    margin-bottom: 16px;
  }

  & .mb-28 {
    margin-bottom: 28px;
  }

  & .mb-36 {
    margin-bottom: 36px;
  }

  & .mb-8 {
    margin-bottom: 8px;
  }

  & .ml-4 {
    margin-left: 4px;
  }

  & .ml-6 {
    margin-left: 6px;
  }

  & .ml-8 {
    margin-left: 8px;
  }

  & .mr-4 {
    margin-right: 4px;
  }

  & .mr-8 {
    margin-right: 8px;
  }

  & .mt-10 {
    margin-top: 10px;
  }

  & .mt-12 {
    margin-top: 12px;
  }

  & .mt-16 {
    margin-top: 16px;
  }

  & .mt-2 {
    margin-top: 2px;
  }

  & .mt-4 {
    margin-top: 4px;
  }

  & .mt-6 {
    margin-top: 6px;
  }

  & .mt-8 {
    margin-top: 8px;
  }

  & .n-c-d-h-100 > div {
    height: 100%;
  }

  & .n-c-d-w-100 > div {
    width: 100%;
  }

  & .p-0-1-1 {
    padding: 0 1px 1px;
  }

  & .p-0-12 {
    padding: 0 12px;
  }

  & .p-0-12-0-6 {
    padding: 0 12px 0 6px;
  }

  & .p-0-14-0-28 {
    padding: 0 14px 0 28px;
  }

  & .p-0-28-0-14 {
    padding: 0 28px 0 14px;
  }

  & .p-0-2 {
    padding: 0 2px;
  }

  & .p-0-4 {
    padding: 0 4px;
  }

  & .p-0-4-0-8 {
    padding: 0 4px 0 8px;
  }

  & .p-0-6-0-12 {
    padding: 0 6px 0 12px;
  }

  & .p-0-8 {
    padding: 0 8px;
  }

  & .p-0-8-0-4 {
    padding: 0 8px 0 4px;
  }

  & .p-1-4 {
    padding: 1px 4px;
  }

  & .p-12 {
    padding: 12px;
  }

  & .p-16-0-16-8 {
    padding: 16px 0 16px 8px;
  }

  & .p-16-8-16-0 {
    padding: 16px 8px 16px 0;
  }

  & .p-16-8-16-16 {
    padding: 16px 8px 16px 16px;
  }

  & .p-16-16-16-8 {
    padding: 16px 16px 16px 8px;
  }

  & .p-2-8 {
    padding: 2px 8px;
  }

  & .p-3-6 {
    padding: 3px 6px;
  }

  & .p-4 {
    padding: 4px;
  }

  & .p-4-0-4-0 {
    padding: 4px 0 4px 0;
  }

  & .p-4-10 {
    padding: 4px 10px;
  }

  & .p-4-16-8-16 {
    padding: 4px 16px 8px 16px;
  }

  & .p-4-8 {
    padding: 4px 8px;
  }

  & .p-5-8 {
    padding: 5px 8px;
  }

  & .p-6-12 {
    padding: 6px 12px;
  }

  & .p-8 {
    padding: 8px;
  }

  & .p-8-10 {
    padding: 8px 10px;
  }

  & .p-8-12 {
    padding: 8px 12px;
  }

  & .p-8-16 {
    padding: 8px 16px;
  }

  & .pb-28 {
    padding-bottom: 28px;
  }

  & .pb-4 {
    padding-bottom: 4px;
  }

  & .pb-8 {
    padding-bottom: 8px;
  }

  & .pl-4 {
    padding-left: 4px;
  }

  & .pl-6 {
    padding-left: 6px;
  }

  & .pos-abs {
    position: absolute;
  }

  & .pos-rel {
    position: relative;
  }

  & .pr-4 {
    padding-right: 4px;
  }

  & .pt-28 {
    padding-top: 28px;
  }

  & .pt-4 {
    padding-top: 4px;
  }

  & .pr-8 {
    padding-right: 8px;
  }

  & .pr-10 {
    padding-right: 10px;
  }

  & .pt-8 {
    padding-top: 8px;
  }

  & .text-blue {
    color: ${themes['@blue-fg']};
  }

  & .text-clickable {
    cursor: pointer;
  }

  & .text-color-f-1 {
    color: #546279;
  }

  & .text-color-f-2 {
    color: rgb(83, 8, 12);
  }

  & .text-danger {
    color: ${themes['@danger-color']};
  }

  & .text-disabled {
    color: rgba(0, 0, 0, .25);
  }

  & .text-end {
    text-align: right;
  }

  & .text-error {
    color: #CF1322;
  }

  & .text-error1 {
    color: #FF4D4F;
  }

  & .text-error2 {
    color: #FF7875;
  }

  & .text-green {
    color: green;
  }

  & .text-grey {
    color: #666;
  }

  & .text-hover-blue:hover {
    color: ${themes['@blue-fg']};
  }

  & .text-info {
    color: ${themes['@info-color']};
  }

  & .text-primary {
    color: ${themes['@primary-color']};
  }

  & .text-statistic-blue {
    color: rgb(44, 105, 154);
  }

  & .text-statistic-green {
    color: rgb(0, 166, 87);
  }

  & .text-statistic-kale {
    color: rgb(24, 74, 78);
  }

  & .text-statistic-purple {
    color: rgb(84, 71, 140);
  }

  & .text-statistic-yellow {
    color: rgb(195, 143, 1);
  }

  & .w-35 {
    width: 35%;
  }

  & .w-40 {
    width: 40%;
  }

  & .w-60 {
    width: 60%;
  }

  & .w-100 {
    width: 100%;
  }

  & .w-none {
    width: auto;
  }

  & .x-scrollable {
    overflow-x: auto;
  }

  & .y-scrollable {
    overflow-y: auto;
  }
`;

export const TextPaddingBox = styled.div`
  padding: ${(props) => props.padding || '8px'};
`;

export const WarningButton = styled(Button)`
  &.ant-btn {
    color: #fff;
    background-color: ${themes['@warning-color']};
    border-color: ${themes['@warning-color']};
  }

  &.ant-btn[disabled], &.ant-btn[disabled]:hover, &.ant-btn[disabled]:focus, &.ant-btn[disabled]:active {
    color: rgba(0, 0, 0, 0.25);
    border-color: #d9d9d9;
    background: #f5f5f5;
    text-shadow: none;
    box-shadow: none;
  }
`;

export const deleteIconStyle = {
  color: '#F00',
};

export const searchBoxInputErrorStyle = {
  border: '1px solid #F00',
  borderRight: 'none',
  borderRadius: 4,
};

export const loadingSpinStyle = {
  fontSize: 24,
  color: themes['@primary-color'],
};

export const messageStyle = {
  textAlign: 'right',
  marginRight: 8,
};

export const verticalSpace = {
  marginTop: REGULAR_SECTION_ROW_VERTICAL_SPACING,
};

export const OpenInNewWindowIcon = styled.i`
  display: inline-block;
  height: 14px;
  width: 14px;
  position: relative;
  margin-right: 8px;

  &:before {
    content: "";	
    position: absolute;
    top: 3px;
    z-index: 2;
    display: inline-block;
    background-color: #fff;
    border: 1px solid #626262;
    border-radius: 2.5px;
    height: 10px;
    width: 10px;
  }

  &:after {
    content: "";	
    position: absolute;
    bottom: -2px;
    left: 5px;
    z-index: 1;
    display: inline-block;
    background-color: #fff;
    border: 1px solid #626262;
    border-radius: 2.5px;
    height: 10px;
    width: 10px;
  }

`;
